<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body text-center">
            <img src="/src/assets/images/logo/vesproc120.png" alt="VESPROC" class="img-fluid mb-3" />

            <!-- Verifying -->
            <template v-if="loading">
              <div class="spinner-border text-primary my-3" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
              <h4 class="f-w-500 mb-1">{{ $t('auth.emailVerification.title') }}</h4>
              <p class="text-muted mb-0">{{ $t('auth.emailVerification.verifying') }}</p>
            </template>

            <!-- Verified: straight on to sign-in -->
            <template v-else-if="verified">
              <div class="auth-status-icon bg-light-success text-success mx-auto mb-3">
                <i class="ph-duotone ph-seal-check"></i>
              </div>
              <h4 class="f-w-500 mb-2">{{ $t('auth.emailVerification.success') }}</h4>
              <p class="mb-3">{{ $t('auth.emailVerification.successMessage') }}</p>
              <p class="text-muted small mb-4" role="status">
                {{ $t('auth.setPassword.redirectCountdown', { seconds: countdown }) }}
              </p>
              <div class="d-grid">
                <router-link :to="{ name: 'Login' }" class="btn btn-primary">
                  {{ $t('auth.emailVerification.goToLogin') }}
                </router-link>
              </div>
            </template>

            <!-- Failed -->
            <template v-else-if="error">
              <div class="auth-status-icon bg-light-danger text-danger mx-auto mb-3">
                <i class="ph-duotone ph-x-circle"></i>
              </div>
              <h4 class="f-w-500 mb-2">{{ $t('auth.emailVerification.error') }}</h4>
              <p class="text-muted mb-4">{{ error }}</p>
              <div class="d-grid">
                <router-link :to="{ name: 'Login' }" class="btn btn-outline-secondary">
                  {{ $t('auth.emailVerification.backToLogin') }}
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  </div>
</template>

<script setup>
// 1. Vue core
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// 3. Services
import authService from '@/services/authService'

// 4. Components
import AuthFooter from '@/components/auth/AuthFooter.vue'

// 5. Constants
const REDIRECT_SECONDS = 5

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const loading = ref(false)
const verified = ref(false)
const error = ref(null)
const countdown = ref(REDIRECT_SECONDS)
let countdownTimer = null

const startRedirectCountdown = () => {
  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer)
      countdownTimer = null
      // replace: going "back" from the login screen should not re-run the verification
      router.replace({ name: 'Login' })
    }
  }, 1000)
}

const verifyEmail = async (token) => {
  loading.value = true
  error.value = null
  try {
    const result = await authService.verifyEmail(token)
    if (result) {
      verified.value = true
      startRedirectCountdown()
    } else {
      error.value = t('auth.emailVerification.verificationFailed')
    }
  } catch (err) {
    console.error('Email verification error:', err)
    error.value = err.response?.data?.error?.message || err.response?.data?.message ||
      t('auth.emailVerification.verificationError')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const token = route.query.token
  if (token) {
    verifyEmail(token)
  } else {
    error.value = t('auth.emailVerification.noToken')
  }
})

onBeforeUnmount(() => {
  if (countdownTimer) clearInterval(countdownTimer)
})
</script>

<style scoped>
.auth-main.v1 {
  background-color: #f2f2f2;
  background-image: url('/src/assets/images/logo/worldmap.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.auth-status-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}
</style>
